import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Youtube from '@tiptap/extension-youtube';
import {
  TextB, TextItalic, TextUnderline, TextStrikethrough, ListBullets,
  ListNumbers, ListChecks, Quotes, Code, Link as LinkIcon, Image as ImageIcon,
  Table as TableIcon, YoutubeLogo as YoutubeIcon, Minus, ArrowCounterClockwise,
  ArrowClockwise, TextHOne, TextHTwo, TextHThree, Palette
} from '@phosphor-icons/react';

const MenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  const addImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const addYouTube = () => {
    const url = window.prompt('Enter YouTube URL:');
    if (url) {
      editor.commands.setYoutubeVideo({ src: url });
    }
  };

  const addTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  return (
    <div className="border border-border rounded-t-lg bg-muted/30 p-2 flex flex-wrap gap-1">
      {/* Text Formatting */}
      <div className="flex gap-1 border-r border-border pr-2">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-muted ${editor.isActive('bold') ? 'bg-primary text-primary-foreground' : ''}`}
          title="Bold"
        >
          <TextB size={18} weight="bold" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-muted ${editor.isActive('italic') ? 'bg-primary text-primary-foreground' : ''}`}
          title="Italic"
        >
          <TextItalic size={18} weight="bold" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-muted ${editor.isActive('underline') ? 'bg-primary text-primary-foreground' : ''}`}
          title="Underline"
        >
          <TextUnderline size={18} weight="bold" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`p-2 rounded hover:bg-muted ${editor.isActive('strike') ? 'bg-primary text-primary-foreground' : ''}`}
          title="Strikethrough"
        >
          <TextStrikethrough size={18} weight="bold" />
        </button>
      </div>

      {/* Headings */}
      <div className="flex gap-1 border-r border-border pr-2">
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`p-2 rounded hover:bg-muted ${editor.isActive('heading', { level: 1 }) ? 'bg-primary text-primary-foreground' : ''}`}
          title="Heading 1"
        >
          <TextHOne size={18} weight="bold" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-2 rounded hover:bg-muted ${editor.isActive('heading', { level: 2 }) ? 'bg-primary text-primary-foreground' : ''}`}
          title="Heading 2"
        >
          <TextHTwo size={18} weight="bold" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`p-2 rounded hover:bg-muted ${editor.isActive('heading', { level: 3 }) ? 'bg-primary text-primary-foreground' : ''}`}
          title="Heading 3"
        >
          <TextHThree size={18} weight="bold" />
        </button>
      </div>

      {/* Lists */}
      <div className="flex gap-1 border-r border-border pr-2">
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-muted ${editor.isActive('bulletList') ? 'bg-primary text-primary-foreground' : ''}`}
          title="Bullet List"
        >
          <ListBullets size={18} weight="bold" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-muted ${editor.isActive('orderedList') ? 'bg-primary text-primary-foreground' : ''}`}
          title="Numbered List"
        >
          <ListNumbers size={18} weight="bold" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          className={`p-2 rounded hover:bg-muted ${editor.isActive('taskList') ? 'bg-primary text-primary-foreground' : ''}`}
          title="Task List"
        >
          <ListChecks size={18} weight="bold" />
        </button>
      </div>

      {/* Blocks */}
      <div className="flex gap-1 border-r border-border pr-2">
        <button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`p-2 rounded hover:bg-muted ${editor.isActive('blockquote') ? 'bg-primary text-primary-foreground' : ''}`}
          title="Quote"
        >
          <Quotes size={18} weight="bold" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={`p-2 rounded hover:bg-muted ${editor.isActive('codeBlock') ? 'bg-primary text-primary-foreground' : ''}`}
          title="Code Block"
        >
          <Code size={18} weight="bold" />
        </button>
        <button
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className="p-2 rounded hover:bg-muted"
          title="Horizontal Line"
        >
          <Minus size={18} weight="bold" />
        </button>
      </div>

      {/* Media */}
      <div className="flex gap-1 border-r border-border pr-2">
        <button
          onClick={addImage}
          className="p-2 rounded hover:bg-muted"
          title="Insert Image"
        >
          <ImageIcon size={18} weight="bold" />
        </button>
        <button
          onClick={addLink}
          className={`p-2 rounded hover:bg-muted ${editor.isActive('link') ? 'bg-primary text-primary-foreground' : ''}`}
          title="Insert Link"
        >
          <LinkIcon size={18} weight="bold" />
        </button>
        <button
          onClick={addYouTube}
          className="p-2 rounded hover:bg-muted"
          title="Embed YouTube"
        >
          <YoutubeIcon size={18} weight="bold" />
        </button>
        <button
          onClick={addTable}
          className="p-2 rounded hover:bg-muted"
          title="Insert Table"
        >
          <TableIcon size={18} weight="bold" />
        </button>
      </div>

      {/* Colors */}
      <div className="flex gap-1 border-r border-border pr-2">
        <input
          type="color"
          onInput={(e) => editor.chain().focus().setColor(e.target.value).run()}
          value={editor.getAttributes('textStyle').color || '#000000'}
          className="w-8 h-8 rounded cursor-pointer"
          title="Text Color"
        />
        <button
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          className={`p-2 rounded hover:bg-muted ${editor.isActive('highlight') ? 'bg-yellow-200' : ''}`}
          title="Highlight"
        >
          <Palette size={18} weight="bold" />
        </button>
      </div>

      {/* Undo/Redo */}
      <div className="flex gap-1">
        <button
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className="p-2 rounded hover:bg-muted disabled:opacity-30"
          title="Undo"
        >
          <ArrowCounterClockwise size={18} weight="bold" />
        </button>
        <button
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className="p-2 rounded hover:bg-muted disabled:opacity-30"
          title="Redo"
        >
          <ArrowClockwise size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
};

export default function EnhancedRichTextEditor({ content, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline',
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Underline,
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Youtube.configure({
        width: 640,
        height: 480,
      }),
    ],
    content: content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-xl max-w-none focus:outline-none min-h-[400px] p-4',
      },
    },
  });

  return (
    <div className="border border-border rounded-lg">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} className="bg-background" />
      
      <style>{`
        .ProseMirror {
          min-height: 400px;
          padding: 1rem;
        }
        
        .ProseMirror:focus {
          outline: none;
        }

        .ProseMirror table {
          border-collapse: collapse;
          table-layout: fixed;
          width: 100%;
          margin: 1rem 0;
          overflow: hidden;
        }

        .ProseMirror table td,
        .ProseMirror table th {
          border: 2px solid #cbd5e0;
          padding: 0.5rem;
          vertical-align: top;
          box-sizing: border-box;
          position: relative;
        }

        .ProseMirror table th {
          background-color: #f7fafc;
          font-weight: bold;
          text-align: left;
        }

        .ProseMirror ul[data-type="taskList"] {
          list-style: none;
          padding: 0;
        }

        .ProseMirror ul[data-type="taskList"] li {
          display: flex;
          align-items: flex-start;
        }

        .ProseMirror ul[data-type="taskList"] li input[type="checkbox"] {
          margin-right: 0.5rem;
          margin-top: 0.25rem;
        }

        .ProseMirror iframe {
          border: 0;
          width: 100%;
          max-width: 640px;
          height: 360px;
        }

        .ProseMirror img {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 1rem 0;
        }

        .ProseMirror blockquote {
          border-left: 4px solid #e2e8f0;
          padding-left: 1rem;
          margin: 1rem 0;
          font-style: italic;
          color: #64748b;
        }

        .ProseMirror pre {
          background: #1e293b;
          color: #e2e8f0;
          font-family: 'JetBrainsMono', 'Courier New', monospace;
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
        }

        .ProseMirror code {
          background: #f1f5f9;
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-size: 0.875rem;
        }

        .ProseMirror hr {
          border: none;
          border-top: 2px solid #e2e8f0;
          margin: 2rem 0;
        }
      `}</style>
    </div>
  );
}
